// Écran d'appel des devis sans réponse — pensé pour le TÉLÉPHONE.
//
// Le tableau du Pilotage sert à voir l'ensemble sur un écran large. Celui-ci
// sert à AGIR, debout, avec un pouce : une fiche à la fois, un gros bouton pour
// appeler, et quatre boutons pour noter ce que le client a répondu.
//
// Le CRM étant une PWA, cette page s'ajoute à l'écran d'accueil du téléphone et
// s'ouvre en plein écran, comme une application.

import { dernierAppel, noterAppel } from '@imp/shared/relances-devis';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Phone, RotateCcw, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  type DevisExterne,
  listDevisExternes,
  type StatutDevis,
  updateDevisExterne,
} from '@/modules/messagerie/relances-api';

const JOUR = 86_400_000;

/** Numéro utilisable par le téléphone : « 06 71 17 08 18 » -> « 0671170818 ». */
function numeroAppelable(tel: string): string {
  return tel.replace(/[^\d+]/g, '');
}

/** Ancienneté d'un devis, en jours, ou null s'il n'a pas de date. */
function anciennete(dateEnvoi: string | null): number | null {
  if (!dateEnvoi) return null;
  const t = Date.parse(`${dateEnvoi}T12:00:00`);
  return Number.isNaN(t) ? null : Math.floor((Date.now() - t) / JOUR);
}

/** « il y a 3 mois », en clair. */
function depuis(jours: number | null): string {
  if (jours === null) return 'date inconnue';
  // Un devis daté dans le futur affichait « il y a -73 jours ». Ce n'est pas
  // une durée, c'est une date fausse : on le dit, au lieu de la mettre en forme.
  if (jours < 0)
    return `daté dans ${String(-jours)} jour${jours < -1 ? 's' : ''} — date à vérifier`;
  if (jours === 0) return "aujourd'hui";
  if (jours < 31) return `il y a ${String(jours)} jour${jours > 1 ? 's' : ''}`;
  const mois = Math.round(jours / 30);
  if (mois < 24) return `il y a ${String(mois)} mois`;
  return `il y a ${String(Math.round(jours / 365))} ans`;
}

const EUR = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });

const FENETRES: { id: string; label: string; jours: number }[] = [
  { id: '6m', label: '6 mois', jours: 180 },
  { id: '12m', label: '1 an', jours: 365 },
  { id: 'tous', label: 'Tous', jours: Number.MAX_SAFE_INTEGER },
];

export function RelancesMobilePage() {
  const queryClient = useQueryClient();
  const [fenetre, setFenetre] = useState('6m');
  const [tri, setTri] = useState<'recent' | 'montant'>('recent');
  // Devis traités pendant cette session : retirés de la pile, sans recharger.
  const [traites, setTraites] = useState<string[]>([]);

  const devisQ = useQuery({ queryKey: ['devis-externes'], queryFn: listDevisExternes });

  const majMut = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: { statut?: StatutDevis; commentaire?: string };
    }) => updateDevisExterne(id, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['devis-externes'] });
    },
  });

  const pile = useMemo(() => {
    const jours = FENETRES.find((f) => f.id === fenetre)?.jours ?? 180;
    const liste = (devisQ.data ?? []).filter((d) => {
      if (d.statut !== 'sans_reponse') return false;
      if (!d.telephone) return false;
      if (traites.includes(d.id)) return false;
      const age = anciennete(d.date_envoi);
      return age !== null && age <= jours;
    });
    // Jamais appelés d'abord, puis les moins récemment tentés. Sans cela, les
    // numéros qui ne décrochent jamais remonteraient en tête à chaque
    // ouverture, et les autres ne seraient jamais appelés.
    return liste.sort((a, b) => {
      const appelA = dernierAppel(a.commentaire);
      const appelB = dernierAppel(b.commentaire);
      if (appelA !== appelB) {
        if (appelA === null) return -1;
        if (appelB === null) return 1;
        return appelA.localeCompare(appelB);
      }
      return tri === 'montant'
        ? b.montant - a.montant
        : (anciennete(a.date_envoi) ?? 0) - (anciennete(b.date_envoi) ?? 0);
    });
  }, [devisQ.data, fenetre, tri, traites]);

  const enJeu = pile.reduce((t, d) => t + d.montant, 0);

  /** Note l'issue de l'appel et retire la fiche de la pile. */
  const repondre = (d: DevisExterne, statut: StatutDevis, texte: string) => {
    majMut.mutate({
      id: d.id,
      patch: { statut, commentaire: noterAppel(d.commentaire, texte, new Date()) },
    });
    setTraites((t) => [...t, d.id]);
  };

  return (
    <div className="mx-auto max-w-lg space-y-3">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Devis à relancer</h1>
        <p className="text-muted-foreground text-sm">
          {pile.length} à appeler · {EUR.format(enJeu)} € en jeu
          {traites.length > 0 ? ` · ${String(traites.length)} traité(s)` : ''}
        </p>
      </div>

      {/* Réglages de la pile : période et ordre d'appel. */}
      <div className="flex flex-wrap gap-1">
        {FENETRES.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => {
              setFenetre(f.id);
            }}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              fenetre === f.id
                ? 'bg-primary text-primary-foreground'
                : 'border-input bg-card text-foreground border'
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => {
            setTri(tri === 'recent' ? 'montant' : 'recent');
          }}
          className="border-input bg-card text-foreground rounded-md border px-3 py-1.5 text-sm font-medium"
          title="Changer l'ordre d'appel"
        >
          {tri === 'recent' ? 'Plus récents' : 'Plus gros montants'}
        </button>
      </div>

      {devisQ.isPending ? (
        <p className="text-muted-foreground text-sm">Chargement…</p>
      ) : pile.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="font-medium text-emerald-700">✓ Rien à appeler sur cette période.</p>
            <p className="text-muted-foreground mt-1 text-sm">
              Élargissez la période, ou passez à autre chose — c&apos;est mérité.
            </p>
          </CardContent>
        </Card>
      ) : (
        pile.map((d) => {
          const age = anciennete(d.date_envoi);
          const dejaAppele = dernierAppel(d.commentaire);
          return (
            <Card key={d.id}>
              <CardContent className="space-y-3 pt-5">
                <div>
                  <div className="text-base font-semibold leading-tight">
                    {d.client ?? 'Client sans nom'}
                  </div>
                  <div className="text-muted-foreground text-sm">
                    {d.ville ?? ''} · {d.numero} · {depuis(age)}
                  </div>
                  {dejaAppele ? (
                    <div className="mt-1 text-xs font-medium text-amber-700">
                      Déjà appelé le{' '}
                      {new Date(`${dejaAppele}T12:00:00`).toLocaleDateString('fr-FR')}
                    </div>
                  ) : null}
                </div>

                <div className="text-sm">{d.objet ?? 'Objet non précisé'}</div>
                <div className="text-lg font-semibold tabular-nums">
                  {EUR.format(d.montant)} € TTC
                </div>

                {d.commentaire ? (
                  <div className="text-muted-foreground bg-muted/50 whitespace-pre-line rounded p-2 text-xs">
                    {d.commentaire}
                  </div>
                ) : null}

                {/* Appel direct : le téléphone compose le numéro. */}
                <a
                  href={`tel:${numeroAppelable(d.telephone ?? '')}`}
                  className="bg-action text-action-foreground hover:bg-action-hover flex h-14 items-center justify-center gap-2 rounded-lg text-lg font-semibold"
                >
                  <Phone className="h-5 w-5" /> {d.telephone}
                </a>

                {/* Ce que le client a répondu. Chaque bouton note et passe au suivant. */}
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    className="h-12 border-emerald-300 text-emerald-700"
                    onClick={() => {
                      repondre(d, 'accepte', 'accepté');
                    }}
                  >
                    <Check className="h-4 w-4" /> Accepté
                  </Button>
                  <Button
                    variant="outline"
                    className="h-12 border-red-300 text-red-700"
                    onClick={() => {
                      repondre(d, 'refuse', 'refusé');
                    }}
                  >
                    <X className="h-4 w-4" /> Refusé
                  </Button>
                  <Button
                    variant="outline"
                    className="h-12 border-sky-300 text-sky-700"
                    onClick={() => {
                      repondre(d, 'en_attente', 'à rappeler');
                    }}
                  >
                    <RotateCcw className="h-4 w-4" /> À rappeler
                  </Button>
                  <Button
                    variant="outline"
                    className="h-12"
                    onClick={() => {
                      // Reste « sans réponse », mais la trace de l'appel est gardée :
                      // sinon on rappellerait indéfiniment le même numéro.
                      repondre(d, 'sans_reponse', 'pas de réponse');
                    }}
                  >
                    Pas de réponse
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}
